import { curveMonotoneX } from '@visx/curve'
import { GlyphDot } from '@visx/glyph'
import { LinearGradient } from '@visx/gradient'
import { Group } from '@visx/group'
import { scaleOrdinal, scaleTime } from '@visx/scale'
import { defaultStyles } from '@visx/tooltip'
import { XYChart, Tooltip, buildChartTheme, AreaSeries, Axis, Grid, GlyphSeries, GlyphProps } from '@visx/xychart'
import { RenderTooltipGlyphProps } from '@visx/xychart/lib/components/Tooltip'
import { isEmpty } from 'lodash'
import { useEffect, useMemo } from 'react'
import Tick from '../Tick'
import { chartHueStyles, hueOutline, hueTint } from 'lib/chartColors'
import type { ChartHue } from 'app/types/chartColors'
import { Row } from 'app/types/qualified-leads'
import { capitalizeCamelCase } from 'app/utils'

const defaultMargin = { top: 40, right: 30, bottom: 40, left: 40 }
// The fill fades from the funnel's first-stage tint to its halo, so the two charts share one blue scale.
const AREA_FILL_TOP_ALPHA = 0.32
const AREA_FILL_BOTTOM_ALPHA = 0.12
const tickLabelProps = {
  fontFamily: `InterVariable, Inter, sans-serif`,
  className: `fill-current text-body-xs text-default`,
}
const EMPTY_SERIES: AreaSeriesProps['series'] = []

const tooltipStyles = {
  ...defaultStyles,
  minWidth: 180,
}

// Role-based replacement for visx's built-in `lightTheme`: axis, grid and label colors
// resolve against the current theme's CSS variables instead of a fixed light palette.
// Series colors themselves come from lib/chartColors per-series, not from this theme.
// `gridColor` also doubles as the Tooltip's vertical-crosshair stroke (xychart reads
// `theme.gridStyles.stroke` for it, see the Tooltip below); the `<Grid>` itself overrides
// this with an explicit `stroke`, but the crosshair does not, so this needs to stay dark
// enough to read as a line rather than as subtle as the grid.
const chartTheme = buildChartTheme({
  backgroundColor: `var(--surface)`,
  colors: [`var(--primary-default)`],
  gridColor: `var(--fg-minimal)`,
  gridColorDark: `var(--line-default)`,
  tickLength: 4,
  svgLabelSmall: { fill: `var(--fg-subtle)` },
  svgLabelBig: { fill: `var(--fg-strong)` },
})

export type GraphSeries = Row & { tooltip: string | number | Date }

type AreaSeriesProps = {
  width: number
  height: number
  series: GraphSeries[]
}

export default function AreaSeriesGraph({ width, height, series = EMPTY_SERIES }: AreaSeriesProps) {
  const data = series ?? []
  const keys = useMemo(() => {
    if (data.length) {
      return Object.keys(data[0])
        .filter(
          (key) =>
            key !== `date` && key !== `tooltip` && key !== `scheduled` && key !== `leads` && key !== `__typename`,
        )
        .sort()
    } else return []
  }, [data])

  // The series is a chip stretched across time: outline ink for the line, tint beneath it, chip dots at the points.
  const getHue = useMemo(
    () =>
      scaleOrdinal<string, ChartHue>({
        domain: keys,
        range: [`blue`, `graphite`],
      }),
    [keys],
  )

  // `style` (not `fill`/`stroke` attributes) so the `var(--surface)`/`color-mix()` values
  // resolve reliably as CSS rather than as SVG presentation attributes.
  const renderTooltipGlyph = ({ x, y, size, key }: RenderTooltipGlyphProps<any>) => (
    <GlyphDot
      left={x}
      top={y}
      r={size}
      style={{ fill: hueOutline(getHue(key)), stroke: `var(--surface)` }}
      strokeWidth={1.5}
    />
  )

  const renderLineGlyph = ({ x, y, key }: GlyphProps<any>) => (
    <GlyphDot
      left={x}
      top={y}
      r={2.5}
      style={{ fill: chartHueStyles[getHue(key)].fill, stroke: hueOutline(getHue(key)) }}
      strokeWidth={1}
    />
  )

  const getTimeScaleDomain = (): Date[] => {
    let timeDomain = data.reduce((acc: any[], item: Row, i) => {
      if (data.length > 54) {
        return i % 5 === 0 ? acc.concat(item.date) : acc
      } else {
        return acc.concat(item.date)
      }
    }, [])
    return timeDomain
  }

  // const getSeriesMaxValue = (): number => {
  //   let percentageValues: number[] = data.reduce(
  //     (acc: any[], item: Serie) =>
  //       acc.concat(
  //         Object.entries(item).reduce((acc, [k, v]) => {
  //           if (k !== `date`) {
  //             acc = +v > acc ? +v : acc
  //           }
  //           return Math.floor(acc)
  //         }, 0),
  //       ),
  //     [],
  //   )
  //   //If max value is 0 then use a max of 1 to avoid rendering a single line
  //   return max(percentageValues) || 1
  // }

  const timeScale = scaleTime<number>({
    domain: getTimeScaleDomain(),
    nice: true,
  })

  const yMax = height - defaultMargin.top
  useEffect(() => {
    if (isEmpty(series)) return
  }, [series])

  return (
    <div className="relative">
      <XYChart
        accessibilityLabel="area series graph"
        theme={chartTheme}
        xScale={{ type: `band`, paddingInner: 0.3 }}
        yScale={{
          type: `linear`,
          domain: [0, 100],
        }}
        height={yMax}
        width={width}
        margin={defaultMargin}
      >
        {/* `stroke`, not `color`: lines only paint a stroke, and `lineStyle` lands in the <line>'s
            own `style`, where `var()` resolves reliably (unlike a `stroke` attribute). */}
        <Grid rows={true} columns={false} lineStyle={{ stroke: `var(--line-subtle)` }} numTicks={6} className="-z-10" />
        {[...keys]?.map((type, i) => (
          <Group key={type}>
            {/* `LinearGradient`'s own `from`/`to` props render as `stop-color` attributes, which
                cannot carry a `var(--surface)`/`color-mix()` value reliably; passing `children`
                (its supported escape hatch) draws the stops ourselves via `style` instead. */}
            <LinearGradient id={`area-fill-${type}`}>
              <stop offset="0%" style={{ stopColor: hueTint(getHue(type), AREA_FILL_TOP_ALPHA) }} />
              <stop offset="100%" style={{ stopColor: hueTint(getHue(type), AREA_FILL_BOTTOM_ALPHA) }} />
            </LinearGradient>
            <AreaSeries
              renderLine
              height={yMax}
              dataKey={type}
              data={data}
              xAccessor={(d) => d[`date`]}
              yAccessor={(d) => d[type]}
              fill={`url(#area-fill-${type})`}
              fillOpacity={1}
              curve={curveMonotoneX}
              lineProps={{ stroke: hueOutline(getHue(type)), strokeWidth: 1.5 }}
            />
            <GlyphSeries
              dataKey={type}
              data={data}
              xAccessor={(d) => d[`date`]}
              yAccessor={(d) => d[type]}
              renderGlyph={renderLineGlyph}
            />
          </Group>
        ))}
        <Axis
          hideAxisLine
          hideTicks
          orientation="left"
          key="percentage-axis"
          numTicks={6}
          stroke={`d2d2d2`}
          tickStroke={`abcbca`}
          tickLabelProps={() => tickLabelProps}
          tickFormat={(d) => `${d}%`}
        />
        <Axis
          hideAxisLine
          orientation="bottom"
          key="time-axis"
          stroke={`d2d2d2`}
          tickStroke={`abcbca`}
          numTicks={timeScale.domain().length}
        >
          {({ ticks }) => <Tick ticks={ticks} />}
        </Axis>
        <Tooltip
          snapTooltipToDatumX
          snapTooltipToDatumY
          showVerticalCrosshair
          showSeriesGlyphs
          showDatumGlyph
          renderGlyph={renderTooltipGlyph}
          style={tooltipStyles}
          className="graph-tooltip-wrapper fixed z-50"
          renderTooltip={({ tooltipData }) => (
            <div>
              <div className="text-ui-xs inverse-text-strong">{tooltipData?.nearestDatum?.datum?.[`tooltip`]}</div>
              {Object.keys(tooltipData?.datumByKey ?? {}).map((leadStatus, i) => {
                return (
                  <div key={leadStatus} className="mt-2 flex justify-between">
                    <div className="flex items-center">
                      <div
                        style={{
                          backgroundColor: chartHueStyles[getHue(leadStatus)].fill,
                          borderColor: chartHueStyles[getHue(leadStatus)].stroke,
                        }}
                        className="h-2 w-2 rounded-full border"
                      ></div>
                      <div className="ml-1.5 text-body-xs capitalize inverse-text-default">
                        {capitalizeCamelCase(leadStatus)}
                      </div>
                    </div>
                    <div className="text-body-xs inverse-text-default">
                      {tooltipData?.nearestDatum?.datum?.[leadStatus] ?? 0}%
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        />
      </XYChart>
    </div>
  )
}
